import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IConversationEnriched, IMessage } from '@eagerdog/interfaces';
import { messageService, IRecipientInfo } from '../../services/message.service';
import { helperService } from '../../services/helper.service';
import { useMessages } from '../../context/MessagesContext';
import MessageBubble from './MessageBubble';
import styles from './Messages.module.scss';

interface IProps {
  conversation: IConversationEnriched | null;
  /**
   * When set, this is a new conversation that hasn't been created yet.
   * Holds either a dog_id (default) or a user _id (when pendingRecipientIsUserId=true).
   */
  pendingDogId: string | null;
  /** When true, pendingDogId is treated as a recipient user _id instead of a dog_id. */
  pendingRecipientIsUserId?: boolean;
  /** Display name of the pending recipient — shown in the header before the first message is sent */
  pendingRecipientName?: string | null;
  onConversationCreated: (conv: IConversationEnriched) => void;
}

const getInitials = (name: string): string => {
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const ConversationPanel: React.FC<IProps> = ({
  conversation,
  pendingDogId,
  pendingRecipientIsUserId = false,
  pendingRecipientName,
  onConversationCreated,
}) => {
  const { refreshUnreadCount, setConversations } = useMessages();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [recipientInfo, setRecipientInfo] = useState<IRecipientInfo | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Track which conversation IDs we've already marked as read to prevent loops
  const markedReadRef = useRef<Set<string>>(new Set());
  const currentUser = helperService.getUser();

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  // Fetch recipient info when entering new-conversation mode
  useEffect(() => {
    if (!pendingDogId) {
      setRecipientInfo(null);
      return;
    }
    const call = pendingRecipientIsUserId
      ? messageService.getRecipientInfoByUserId(pendingDogId)
      : messageService.getRecipientInfo(pendingDogId);
    call.then(setRecipientInfo).catch(() => {});
  }, [pendingDogId, pendingRecipientIsUserId]);

  // Load messages when conversation changes
  useEffect(() => {
    if (!conversation) {
      setMessages([]);
      setHasMore(false);
      return;
    }

    setLoading(true);
    setMessages([]);
    messageService
      .getMessages(conversation._id)
      .then((msgs) => {
        setMessages(msgs);
        setHasMore(msgs.length === 50);
        setTimeout(scrollToBottom, 50);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // Mark as read — only once per conversation open to avoid render loops
    if (!markedReadRef.current.has(conversation._id)) {
      markedReadRef.current.add(conversation._id);
      messageService.markAsRead(conversation._id).then(() => {
        refreshUnreadCount();
        setConversations((prev) =>
          prev.map((c) => (c._id === conversation._id ? { ...c, unread_count: 0 } : c)),
        );
      });
    }
  }, [conversation, refreshUnreadCount, scrollToBottom]);

  // Listen for new messages on this conversation
  useEffect(() => {
    if (!conversation) return;

    const off = messageService.onNewMessage((msg, updatedConv) => {
      if (msg.conversation_id !== conversation._id) return;
      setMessages((prev) => [...prev, msg]);
      setTimeout(scrollToBottom, 50);

      // Mark as read immediately since panel is open — allow re-firing for new messages
      markedReadRef.current.delete(conversation._id);
      markedReadRef.current.add(conversation._id);
      messageService.markAsRead(conversation._id).then(() => {
        refreshUnreadCount();
        setConversations((prev) =>
          prev.map((c) => (c._id === conversation._id ? { ...c, unread_count: 0 } : c)),
        );
      });

      // Update conversation list
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c._id === updatedConv._id);
        if (idx === -1) return [updatedConv, ...prev];
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...updatedConv, unread_count: 0 };
        return updated;
      });
    });

    return off;
  }, [conversation, refreshUnreadCount, scrollToBottom, setConversations]);

  const loadMore = async () => {
    if (!conversation || messages.length === 0) return;
    const oldest = messages[0]._id;
    const older = await messageService.getMessages(conversation._id, oldest);
    setMessages((prev) => [...older, ...prev]);
    setHasMore(older.length === 50);
  };

  const handleSend = async () => {
    const text = content.trim();
    if (!text || sending) return;

    setSending(true);

    try {
      if (pendingDogId) {
        // First message — create conversation atomically
        const { conversation: newConv, message } = await messageService.initiateConversation(
          text,
          pendingRecipientIsUserId ? pendingDogId : undefined,
          pendingRecipientIsUserId ? undefined : pendingDogId,
        );
        setMessages([message]);
        onConversationCreated(newConv);
      } else if (conversation) {
        const msg = await messageService.sendMessage(conversation._id, text);
        setMessages((prev) => [...prev, msg]);

        // Update conversation preview in list
        setConversations((prev) =>
          prev.map((c) =>
            c._id === conversation._id
              ? { ...c, last_message_preview: text, last_message_at: msg.created_at }
              : c,
          ),
        );
      }
      setContent('');
      setTimeout(scrollToBottom, 50);
    } catch {
      // TODO: toast error
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const title = conversation?.other_user.display_name
    ?? recipientInfo?.display_name
    ?? pendingRecipientName
    ?? (pendingDogId ? '' : '');
  const subtitle = conversation?.other_user.email ?? recipientInfo?.email ?? null;

  if (!conversation && !pendingDogId) {
    return (
      <div className={styles.conversationPanel}>
        <div className={styles.panelEmpty}>Select a conversation to get started</div>
      </div>
    );
  }

  return (
    <div className={styles.conversationPanel}>
      {(title || pendingDogId) && (
        <div className={styles.threadHeader}>
          <div className={styles.threadAvatar}>
            {conversation?.other_user.logo_url ? (
              <img
                src={conversation.other_user.logo_url}
                alt={title}
                className={styles.threadAvatarImg}
              />
            ) : (
              title ? getInitials(title) : '?'
            )}
          </div>
          <div className={styles.threadHeaderInfo}>
            <span className={styles.threadName}>{title || 'Loading...'}</span>
            {subtitle && <span className={styles.threadEmail}>{subtitle}</span>}
          </div>
        </div>
      )}

      <div className={styles.messageScroll} ref={scrollRef}>
        {hasMore && (
          <button className={styles.loadMoreBtn} onClick={loadMore}>
            Load older messages
          </button>
        )}
        {loading && <div className={styles.panelEmpty}>Loading...</div>}
        {messages.map((msg) => (
          <MessageBubble
            key={msg._id}
            message={msg}
            currentUserId={currentUser._id}
          />
        ))}
        {!loading && messages.length === 0 && pendingDogId && (
          <div className={styles.panelEmpty}>Send a message to start the conversation.</div>
        )}
      </div>

      <div className={styles.typingArea}>
        <div className={styles.typingInner}>
          <div className={styles.typingRow}>
            <textarea
              placeholder="Type a message..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <button onClick={handleSend} disabled={sending || !content.trim()}>
              {sending ? '...' : 'Send'}
            </button>
          </div>
          <span className={styles.typingHint}>Enter to send · Shift+Enter for new line</span>
        </div>
      </div>
    </div>
  );
};

export default ConversationPanel;
